import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  type ISignUpResult,
  type CognitoUserSession as Session,
} from "amazon-cognito-identity-js";
import { env } from "../env";

const POOL = new CognitoUserPool({
  UserPoolId: env.userPoolId,
  ClientId: env.userPoolClientId,
});

// "Remember Me" — the SDK by default uses localStorage. We override the
// storage so a non-persisted login lives in sessionStorage and is wiped on
// browser close.
const inMemoryStores = new WeakMap<object, Record<string, string>>();

class SessionStorageShim {
  private mem: Record<string, string> = {};
  getItem(k: string) {
    return Object.prototype.hasOwnProperty.call(this.mem, k) ? this.mem[k] : null;
  }
  setItem(k: string, v: string) {
    this.mem[k] = v;
  }
  removeItem(k: string) {
    delete this.mem[k];
  }
  clear() {
    this.mem = {};
  }
}

const transientStore = new SessionStorageShim();

const buildPool = (remember: boolean) =>
  new CognitoUserPool({
    UserPoolId: env.userPoolId,
    ClientId: env.userPoolClientId,
    // window.localStorage is the default, but make it explicit
    Storage: remember
      ? (window.localStorage as any)
      : (transientStore as any),
  });

export const signUp = async (email: string, password: string, name: string) =>
  new Promise<ISignUpResult>((resolve, reject) => {
    POOL.signUp(
      email,
      password,
      [
        new CognitoUserAttribute({ Name: "email", Value: email }),
        new CognitoUserAttribute({ Name: "given_name", Value: name }),
      ],
      [],
      (err, res) => (err || !res ? reject(err) : resolve(res)),
    );
  });

export const confirmSignUp = async (email: string, code: string) =>
  new Promise<void>((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: POOL });
    user.confirmRegistration(code, true, (err) => (err ? reject(err) : resolve()));
  });

export const resendConfirmation = async (email: string) =>
  new Promise<void>((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: POOL });
    user.resendConfirmationCode((err) => (err ? reject(err) : resolve()));
  });

export const signIn = async (
  email: string,
  password: string,
  remember: boolean,
): Promise<Session> =>
  new Promise((resolve, reject) => {
    const pool = buildPool(remember);
    const user = new CognitoUser({ Username: email, Pool: pool });
    user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });

export const signOut = () => {
  const user = buildPool(true).getCurrentUser();
  if (user) user.signOut();
  const tUser = buildPool(false).getCurrentUser();
  if (tUser) tUser.signOut();
};

export const getCurrentUserSession = (): Promise<Session | null> =>
  new Promise((resolve) => {
    for (const remember of [true, false]) {
      const user = buildPool(remember).getCurrentUser();
      if (!user) continue;
      user.getSession((err: Error | null, session: Session | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }
        resolve(session);
      });
      return;
    }
    resolve(null);
  });

export const getIdToken = async (): Promise<string | null> => {
  const session = await getCurrentUserSession();
  if (!session) return null;
  return session.getIdToken().getJwtToken();
};

export const forgotPassword = (email: string) =>
  new Promise<void>((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: POOL });
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });

export const confirmPassword = (email: string, code: string, password: string) =>
  new Promise<void>((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: POOL });
    user.confirmPassword(code, password, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
